import { computed } from "vue"
import { personalCurrency } from "../stores/currencyPreference"
import { currentHousehold, isHouseholdContext } from "../stores/householdStore"
import { DEFAULT_HOUSEHOLD_CURRENCY } from "../types/household"

// Resolve the grouping locale from the saved language without importing the
// (side-effectful) i18n instance, so this module stays cheap to import from
// services and tests. Mirrors the default used in i18n setup.
function currentLocale(): string {
  const lang = typeof localStorage !== "undefined" ? localStorage.getItem("language") : null
  if (lang === "en") return "en-US"
  if (lang === "no") return "nb-NO"
  // No saved preference: mirror i18n's detectLanguage() device fallback, so
  // number grouping always matches the UI language (an English-device user
  // was getting a Norwegian-grouped "15 406 kr" under an English UI).
  const device = typeof navigator !== "undefined" ? (navigator.language ?? "").toLowerCase() : ""
  return /^(nb|nn|no)(-|$)/.test(device) ? "nb-NO" : "en-US"
}

// Display symbol per supported currency. The Scandinavian currencies all use
// "kr"; everything else falls back to the ISO code if we don't have a glyph.
const CURRENCY_SYMBOLS: Record<string, string> = {
  NOK: "kr",
  SEK: "kr",
  DKK: "kr",
  EUR: "€",
  USD: "$",
  GBP: "£",
}

/** Currencies the UI offers; anything else still formats via its ISO code. */
export const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_SYMBOLS)

/**
 * The currency amounts should be shown in: the selected household's setting
 * when viewing a shared budget, otherwise the user's personal preference,
 * falling back to the app default. A plain computed so it can be read
 * reactively inside component render and lazily from services.
 */
export const activeCurrency = computed(
  () =>
    (isHouseholdContext.value ? currentHousehold.value?.settings?.currency : personalCurrency.value) ||
    DEFAULT_HOUSEHOLD_CURRENCY,
)

/** Symbol for the active currency (e.g. "kr", "€"). Reactive. */
export const currencySymbol = computed(() => CURRENCY_SYMBOLS[activeCurrency.value] ?? activeCurrency.value)

/**
 * Format an amount in symbol-suffix style for the active currency, e.g.
 * "1 234 kr" / "1 234 €". Grouped and integer by default; pass `decimals` for
 * cases like unit prices. Reading the reactive sources here means callers in a
 * component render re-render when the currency or language changes.
 */
export function formatMoney(amount: number, decimals = 0): string {
  return formatMoneyIn(amount, activeCurrency.value, decimals)
}

/**
 * Same suffix style for an explicit currency, for callers that carry their own
 * currency code instead of reading the active one (e.g. the search index).
 */
export function formatMoneyIn(amount: number, currency: string, decimals = 0): string {
  return `${formatAmount(amount, decimals)} ${CURRENCY_SYMBOLS[currency] ?? currency}`
}

/**
 * Grouped number with NO currency suffix, for the "spent / budget" pairs where
 * one unit is printed at the end of the line. Without this the left-hand figure
 * was raw (`n.toFixed(0)`) while the right went through formatMoney, so a
 * single line read "6120 / 8 000 kr" — two formats, three centimetres apart.
 */
export function formatAmount(amount: number, decimals = 0): string {
  return new Intl.NumberFormat(currentLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount ?? 0)
}

export function useCurrency() {
  return { activeCurrency, currencySymbol, formatMoney }
}
